import { useState, useEffect } from 'react';
import { userLogin } from '~/services/auth';

type SessionExpiryModalProps = {
    isOpen: boolean;
    isExpired: boolean;
    userEmail: string;
    onReAuthenticated: (resp: any) => void;
    onLogout: () => void;
};

export const SessionExpiryModal = ({
    isOpen,
    isExpired,
    userEmail,
    onReAuthenticated,
    onLogout,
}: SessionExpiryModalProps) => {
    const [username, setUsername] = useState(userEmail);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // Update username when userEmail prop changes
    useEffect(() => {
        setUsername(userEmail);
    }, [userEmail]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setAuthError(null);
        setSubmitting(true);

        try {
            const resp = await userLogin(username, password);
            if (!resp.user) {
                setAuthError('Incorrect credentials. Please try again.');
                setSubmitting(false);
                return;
            }

            // Store updated token and expiry
            if (resp.token) {
                localStorage.setItem('token', resp.token);
            }
            if (resp.expiry) {
                localStorage.setItem('expiry', resp.expiry);
                const ttl = new Date(resp.expiry).getTime() - Date.now();
                localStorage.setItem('sessionTTL', ttl.toString());
            }
            localStorage.setItem('user', JSON.stringify(resp.user));

            setPassword('');
            setSubmitting(false);
            onReAuthenticated(resp);
        } catch (err) {
            setAuthError('An error occurred. Please try again.');
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black bg-opacity-50" />

            {/* Modal */}
            <div className="relative bg-white dark:bg-boxdark rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
                <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold text-black dark:text-white mb-2">
                        {isExpired ? 'Session Expired' : 'Session Expiring Soon'}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400">
                        {isExpired
                            ? 'Your session has expired. Please sign in again to continue.'
                            : 'Your session is about to expire. Sign in to continue working.'}
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label
                            htmlFor="session-username"
                            className="block text-sm font-medium text-black dark:text-white mb-2"
                        >
                            Username
                        </label>
                        <input
                            id="session-username"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            className="w-full rounded border border-stroke bg-transparent py-2 px-4 outline-none focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white"
                        />
                    </div>

                    <div className="mb-4">
                        <label
                            htmlFor="session-password"
                            className="block text-sm font-medium text-black dark:text-white mb-2"
                        >
                            Password
                        </label>
                        <div className="relative">
                            <input
                                id="session-password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="w-full rounded border border-stroke bg-transparent py-2 px-4 pr-12 outline-none focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:text-white"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                            >
                                {showPassword ? 'Hide' : 'Show'}
                            </button>
                        </div>
                    </div>

                    {authError && (
                        <div className="mb-4 p-3 rounded bg-red-100 text-red-700 text-sm">
                            {authError}
                        </div>
                    )}

                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onLogout}
                            className="flex-1 rounded border border-stroke py-2 px-4 text-black hover:bg-gray-100 dark:border-strokedark dark:text-white dark:hover:bg-meta-4"
                        >
                            Log Out
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="flex-1 rounded bg-primary py-2 px-4 text-white hover:bg-opacity-90 disabled:opacity-50"
                        >
                            {submitting ? 'Signing in...' : 'Sign In'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
